import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Typography, Stack, Chip } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { DPDP_BREACHES } from './data';
import { ORANGE_DK, ORANGE_SOFT, BORDER, BG_SOFT, TXT, TXT_DIM, card, gradientText, primaryBtn, focusRing, fmtFull, fmtCompact } from './theme';

export default function PenaltyCalculator() {
  const navigate = useNavigate();
  const [breachKey, setBreachKey] = useState('security');

  const breach = DPDP_BREACHES.find((b) => b.key === breachKey) || DPDP_BREACHES[0];
  const currency = 'INR';
  const headline = breach.cap;

  const rows = [
    ['Governing provision', breach.section],
    ['Adjudicated by', 'Data Protection Board of India'],
    ['Statutory maximum penalty', fmtFull(breach.cap, 'INR')],
  ];

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
        gap: { xs: 3, md: 4 },
        alignItems: 'start',
      }}
    >
      {/* inputs */}
      <Box sx={{ ...card, p: { xs: 2.5, md: 4 } }}>
        <Stack spacing={2}>
          <Typography sx={{ color: TXT_DIM, fontWeight: 600, fontSize: '0.78rem', letterSpacing: 0.5 }}>
            BREACH TYPE
          </Typography>
          <Stack spacing={1.2} role="radiogroup" aria-label="DPDP Act breach type">
            {DPDP_BREACHES.map((b) => {
              const active = b.key === breachKey;
              return (
                <Box
                  key={b.key}
                  role="radio"
                  aria-checked={active}
                  tabIndex={0}
                  onClick={() => setBreachKey(b.key)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setBreachKey(b.key);
                    }
                  }}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 1.5,
                    p: 2,
                    borderRadius: 2,
                    cursor: 'pointer',
                    transition: 'border-color .2s, background .2s',
                    background: active ? ORANGE_SOFT : '#fff',
                    border: `1px solid ${active ? 'rgba(255,102,0,0.5)' : BORDER}`,
                    ...focusRing,
                    '&:hover': { borderColor: active ? 'rgba(255,102,0,0.5)' : '#CBD5E1' },
                  }}
                >
                  <Box>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: TXT }}>{b.label}</Typography>
                    <Typography sx={{ fontSize: '0.72rem', color: TXT_DIM }}>{b.section}</Typography>
                  </Box>
                  <Chip
                    size="small"
                    label={fmtCompact(b.cap, 'INR')}
                    sx={{
                      fontWeight: 700,
                      fontSize: '0.72rem',
                      color: active ? ORANGE_DK : TXT_DIM,
                      background: active ? 'rgba(255,102,0,0.12)' : BG_SOFT,
                      border: `1px solid ${BORDER}`,
                    }}
                  />
                </Box>
              );
            })}
          </Stack>
          <Typography sx={{ color: TXT_DIM, fontSize: '0.78rem', lineHeight: 1.55, pt: 0.5 }}>
            DPDP penalties are fixed statutory caps. The actual amount is set by the Data Protection Board based on the
            nature, gravity and duration of the breach — it does not scale with revenue or user count.
          </Typography>
        </Stack>
      </Box>

      {/* result */}
      <Box sx={{ ...card, p: { xs: 3, md: 4 }, position: { md: 'sticky' }, top: { md: 96 } }}>
        <Typography sx={{ color: TXT_DIM, fontWeight: 600, fontSize: '0.8rem', letterSpacing: 0.5, mb: 1 }}>
          ESTIMATED MAXIMUM PENALTY
        </Typography>
        <Typography sx={{ fontWeight: 800, fontSize: { xs: '2.8rem', md: '3.4rem' }, lineHeight: 1, ...gradientText }}>
          {fmtCompact(headline, currency)}
        </Typography>

        <Stack spacing={1.2} sx={{ mt: 3 }}>
          {rows.map(([label, value], i) => (
            <Stack
              key={label}
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{
                pt: i === rows.length - 1 ? 1.2 : 0,
                borderTop: i === rows.length - 1 ? `1px solid ${BORDER}` : 'none',
              }}
            >
              <Typography sx={{ color: TXT_DIM, fontSize: '0.85rem' }}>{label}</Typography>
              <Typography sx={{ fontWeight: i === rows.length - 1 ? 800 : 600, fontSize: '0.9rem', color: TXT, fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>
                {value}
              </Typography>
            </Stack>
          ))}
        </Stack>

        <Typography sx={{ color: TXT_DIM, fontSize: '0.82rem', lineHeight: 1.5, mt: 2.5 }}>{breach.basis}</Typography>

        <Button fullWidth sx={{ ...primaryBtn, mt: 2.5 }} endIcon={<ArrowForwardIcon />} onClick={() => navigate('/register')}>
          Lower your risk with CMS
        </Button>

        <Typography sx={{ color: TXT_DIM, fontSize: '0.72rem', mt: 2, lineHeight: 1.5, textAlign: 'center' }}>
          Statutory maximums under the DPDP Act, 2023, shown for illustration only — not legal advice or a prediction of
          actual penalties imposed by the Data Protection Board.
        </Typography>
      </Box>
    </Box>
  );
}
